import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import DocumentNode from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import TextNode from '@tiptap/extension-text';
import { Editor } from '@tiptap/core';

import { TemplateEmailNotificationVariableResponse } from '@/main/models/response/templateEmailNotificationResponse';

import { TemplateVariable, TEMPLATE_VARIABLE_REGEX } from '../extensions/templateVariable';
import { validateVariablePaste, buildVariableLineNodes } from '../variablePaste';
import * as S from '../styles';

interface ISubjectEditorProps {
  id?: string;
  value: string;
  variables: TemplateEmailNotificationVariableResponse[];
  onChange: (value: string) => void;
  cultureName?: string;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Converte o subject (string simples, ex.: `Olá {{Name}}`) em HTML que o editor
 * entende, transformando cada `{{variável}}` em um span de chip. Como o subject é
 * de uma linha só, quebras de linha são colapsadas em espaço.
 */
function subjectToEditorHtml(subject: string): string {
  const singleLine = subject.replace(/\s*\n\s*/g, ' ');
  let inner = '';
  let lastIndex = 0;

  const re = new RegExp(TEMPLATE_VARIABLE_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = re.exec(singleLine)) !== null) {
    if (match.index > lastIndex) {
      inner += escapeHtml(singleLine.slice(lastIndex, match.index));
    }
    inner += `<span data-template-variable data-name="${escapeHtml(match[1].trim())}"></span>`;
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < singleLine.length) {
    inner += escapeHtml(singleLine.slice(lastIndex));
  }

  return `<p>${inner}</p>`;
}

// Documento de um único parágrafo: garante que o subject permaneça em uma linha,
// inclusive ao colar texto com múltiplas linhas via paste nativo.
const SingleLineDocument = DocumentNode.extend({ content: 'paragraph' });

function SubjectEditor({ id, value, variables, onChange, cultureName }: ISubjectEditorProps) {
  const variablesRef = useRef(variables);
  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  const editor = useEditor({
    extensions: [SingleLineDocument, Paragraph, TextNode, TemplateVariable],
    content: subjectToEditorHtml(value),
    editorProps: {
      handleTextInput(_view, _from, _to, text) {
        // Bloqueia digitação manual de chaves; variáveis só entram via colagem.
        return text.includes('{') || text.includes('}');
      },
      handleKeyDown(_view, event) {
        // Campo de uma linha: impede quebra de linha.
        if (event.key === 'Enter') return true;
        return false;
      },
      handlePaste(view, event) {
        const raw = event.clipboardData?.getData('text/plain') ?? '';
        const text = raw.replace(/\s*\n\s*/g, ' ');

        const result = validateVariablePaste(text, variablesRef.current, true, cultureName);
        if (result.status === 'passthrough') return false;
        if (result.status === 'invalid') return true;

        const { schema, tr, selection } = view.state;
        const nodes = buildVariableLineNodes(text, schema);
        if (nodes.length === 0) return true;

        view.dispatch(tr.replaceWith(selection.from, selection.to, nodes).scrollIntoView());
        return true;
      }
    },
    onUpdate: ({ editor: ed }: { editor: Editor }) => {
      onChange(ed.getText());
    }
  });

  return (
    <S.SubjectEditorWrapper id={id}>
      <EditorContent editor={editor} />
    </S.SubjectEditorWrapper>
  );
}

export default SubjectEditor;
